import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import ReactDOM from 'react-dom';
import { observer } from 'mobx-react';
import axios from 'axios';
import { getTutorialSteps } from './tutorialSteps';
import TutorialTooltip from './TutorialTooltip';
import TutorialContext from './TutorialContext';

// Approximate TutorialTooltip card footprint — used only to clamp the tooltip
// back into the viewport when the natural placement would overflow.
const TOOLTIP_W = 360;
const TOOLTIP_H = 280;
const VIEWPORT_PAD = 16;
const NAV_DELAY_MS = 600;
const FIND_RETRY_MS = 100;
const FIND_MAX_ATTEMPTS = 30; // ~3s

const computeTooltipPos = (rect, placement, vw, vh) => {
  let top;
  let left;
  switch (placement) {
    case 'bottom':
      top = rect.bottom + VIEWPORT_PAD;
      left = rect.left;
      break;
    case 'top':
      top = rect.top - TOOLTIP_H - VIEWPORT_PAD;
      left = rect.left;
      break;
    case 'right':
    case 'right-start':
    default:
      top = rect.top;
      left = rect.right + VIEWPORT_PAD;
      break;
  }
  if (left + TOOLTIP_W > vw - VIEWPORT_PAD) left = vw - TOOLTIP_W - VIEWPORT_PAD;
  if (top + TOOLTIP_H > vh - VIEWPORT_PAD) top = vh - TOOLTIP_H - VIEWPORT_PAD;
  if (left < VIEWPORT_PAD) left = VIEWPORT_PAD;
  if (top < VIEWPORT_PAD) top = VIEWPORT_PAD;
  return { top, left };
};

// Fallback when a step's target never appears within ~3s — keeps Skip / Next
// reachable instead of leaving the user staring at a dim screen with no UI.
const buildCenterFallbackRect = () => {
  const vw = window.innerWidth;
  const vh = window.innerHeight;
  return {
    top: vh / 2, left: vw / 2, width: 0, height: 0,
    right: vw / 2, bottom: vh / 2,
  };
};

const TutorialTour = ({ currentUser, history, liveMeetingsEnabled, children }) => {
  const steps = useMemo(() => getTutorialSteps(liveMeetingsEnabled), [liveMeetingsEnabled]);
  const [run, setRun] = useState(false);
  const [stepIndex, setStepIndex] = useState(0);
  const [rect, setRect] = useState(null);
  const [viewport, setViewport] = useState({
    w: typeof window !== 'undefined' ? window.innerWidth : 0,
    h: typeof window !== 'undefined' ? window.innerHeight : 0,
  });

  const targetElRef = useRef(null);
  const rafRef = useRef(null);
  const findIntervalRef = useRef(null);
  const startTimerRef = useRef(null);
  const navTimerRef = useRef(null);
  const roRef = useRef(null);

  const step = run ? steps[stepIndex] : null;

  const measure = useCallback(() => {
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
    rafRef.current = requestAnimationFrame(() => {
      const el = targetElRef.current;
      if (!el) return;
      const r = el.getBoundingClientRect();
      setRect({
        top: r.top, left: r.left, width: r.width, height: r.height,
        right: r.right, bottom: r.bottom,
      });
      setViewport({ w: window.innerWidth, h: window.innerHeight });
    });
  }, []);

  // Locate the step's target in the DOM, retrying for ~3s in case the page
  // is mid-mount (cross-page navigation steps land here). Once found, scroll
  // it into view (instant, not smooth — smooth-scroll would let the spotlight
  // settle on the pre-scroll rect) and attach window listeners + a per-target
  // ResizeObserver so the spotlight stays glued through layout changes.
  useEffect(() => {
    if (!run || !step) return undefined;
    setRect(null);
    targetElRef.current = null;
    if (roRef.current) { roRef.current.disconnect(); roRef.current = null; }

    let attempts = 0;
    const tryFind = () => {
      const el = document.querySelector(step.target);
      if (el) {
        if (findIntervalRef.current) {
          clearInterval(findIntervalRef.current);
          findIntervalRef.current = null;
        }
        targetElRef.current = el;
        if (!step.disableScrolling) {
          el.scrollIntoView({ block: 'center', behavior: 'auto' });
        }
        if (typeof ResizeObserver !== 'undefined') {
          roRef.current = new ResizeObserver(measure);
          roRef.current.observe(el);
        }
        measure();
        return true;
      }
      attempts += 1;
      if (attempts >= FIND_MAX_ATTEMPTS) {
        if (findIntervalRef.current) {
          clearInterval(findIntervalRef.current);
          findIntervalRef.current = null;
        }
        // Dead target — surface a centered tooltip so Skip stays reachable.
        // eslint-disable-next-line no-console
        console.warn('[tutorial] target never appeared:', step.target);
        setRect(buildCenterFallbackRect());
      }
      return false;
    };

    if (!tryFind()) {
      findIntervalRef.current = setInterval(tryFind, FIND_RETRY_MS);
    }

    const onResize = () => measure();
    const onScroll = () => measure();
    window.addEventListener('resize', onResize);
    window.addEventListener('scroll', onScroll, true);

    return () => {
      window.removeEventListener('resize', onResize);
      window.removeEventListener('scroll', onScroll, true);
      if (roRef.current) { roRef.current.disconnect(); roRef.current = null; }
      if (findIntervalRef.current) {
        clearInterval(findIntervalRef.current);
        findIntervalRef.current = null;
      }
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, [run, step, measure]);

  useEffect(() => {
    if (currentUser?.hasCompletedTutorial) return undefined;
    startTimerRef.current = setTimeout(() => {
      if (history.location.pathname !== '/dashboard') history.push('/dashboard');
      setStepIndex(0);
      setRun(true);
    }, 1000);
    return () => {
      if (startTimerRef.current) clearTimeout(startTimerRef.current);
    };
  }, [currentUser?.hasCompletedTutorial, history]);

  useEffect(() => () => {
    if (navTimerRef.current) clearTimeout(navTimerRef.current);
  }, []);

  const markCompleted = useCallback(async () => {
    if (!currentUser?.userId) return;
    try {
      await axios.patch(`/users/${currentUser.userId}`, { hasCompletedTutorial: true });
      if (currentUser.setHasCompletedTutorial) currentUser.setHasCompletedTutorial(true);
    } catch (err) {
      console.error('Failed to save tutorial completion:', err);
    }
  }, [currentUser]);

  const finishTour = useCallback((finalNavigate) => {
    // Cancel anything pending — without this, a 600ms NAV_DELAY_MS in flight
    // would fire after Skip and re-open the tour.
    if (navTimerRef.current) { clearTimeout(navTimerRef.current); navTimerRef.current = null; }
    if (findIntervalRef.current) { clearInterval(findIntervalRef.current); findIntervalRef.current = null; }
    if (roRef.current) { roRef.current.disconnect(); roRef.current = null; }
    setRun(false);
    setRect(null);
    targetElRef.current = null;
    markCompleted();
    if (finalNavigate) history.push(finalNavigate);
  }, [history, markCompleted]);

  const restartTutorial = useCallback(() => {
    if (history.location.pathname !== '/dashboard') history.push('/dashboard');
    setStepIndex(0);
    setRun(false);
    if (navTimerRef.current) clearTimeout(navTimerRef.current);
    navTimerRef.current = setTimeout(() => setRun(true), NAV_DELAY_MS);
  }, [history]);

  const goNext = useCallback(() => {
    const current = steps[stepIndex];
    const next = steps[stepIndex + 1];
    if (!next) { finishTour(current?.navigateAfter); return; }
    if (current?.navigateAfter) {
      setRun(false);
      history.push(current.navigateAfter);
      if (navTimerRef.current) clearTimeout(navTimerRef.current);
      navTimerRef.current = setTimeout(() => {
        setStepIndex(stepIndex + 1);
        setRun(true);
      }, NAV_DELAY_MS);
    } else {
      setStepIndex(stepIndex + 1);
    }
  }, [stepIndex, history, finishTour]);

  const goPrev = useCallback(() => {
    const current = steps[stepIndex];
    const prev = steps[stepIndex - 1];
    if (!prev) return;
    if (prev.page !== current.page) {
      setRun(false);
      history.push(prev.page);
      if (navTimerRef.current) clearTimeout(navTimerRef.current);
      navTimerRef.current = setTimeout(() => {
        setStepIndex(stepIndex - 1);
        setRun(true);
      }, NAV_DELAY_MS);
    } else {
      setStepIndex(stepIndex - 1);
    }
  }, [stepIndex, history]);

  const skipTour = useCallback(() => { finishTour(null); }, [finishTour]);

  const ctxValue = useMemo(() => ({ restartTutorial }), [restartTutorial]);

  const renderOverlay = () => {
    if (!run || !step || !rect) return null;
    const { w: vw, h: vh } = viewport;
    const { top, left, width, height } = rect;
    const isLastStep = stepIndex === steps.length - 1;

    // Spotlight cutout: content-area targets get a little breathing-room
    // padding around the rect; sidebar targets keep the tight fit because
    // the NavItem buttons already have their own border-radius and any
    // padding around them would look loose. Both get rounded corners on
    // the un-dimmed area so it matches the tooltip card's rounded look.
    const isSidebarStep = step.target === '[data-tour="sidebar"]'
      || step.target.startsWith('[data-tour="nav-');
    const PAD = isSidebarStep ? 0 : 8;
    const R = 6;
    const cutLeft = Math.max(0, left - PAD);
    const cutTop = Math.max(0, top - PAD);
    const cutW = Math.min(vw - cutLeft, width + PAD * 2);
    const cutH = Math.min(vh - cutTop, height + PAD * 2);
    const paddedRect = {
      top: cutTop, left: cutLeft, width: cutW, height: cutH,
      right: cutLeft + cutW, bottom: cutTop + cutH,
    };
    const pos = computeTooltipPos(paddedRect, step.placement, vw, vh);

    return ReactDOM.createPortal(
      <div style={{ position: 'fixed', inset: 0, zIndex: 10000, pointerEvents: 'none' }}>
        <svg
          style={{ position: 'absolute', top: 0, left: 0 }}
          width={vw}
          height={vh}
          aria-hidden="true"
        >
          <defs>
            <mask id="tutorial-spotlight-mask">
              <rect width={vw} height={vh} fill="white" />
              <rect x={cutLeft} y={cutTop} width={cutW} height={cutH} rx={R} ry={R} fill="black" />
            </mask>
          </defs>
          <rect width={vw} height={vh} fill="rgba(0,0,0,0.85)" mask="url(#tutorial-spotlight-mask)" />
        </svg>
        <TutorialTooltip
          continuous
          index={stepIndex}
          step={step}
          size={steps.length}
          isLastStep={isLastStep}
          backProps={{ onClick: goPrev, 'aria-label': 'Back' }}
          primaryProps={{ onClick: goNext, 'aria-label': isLastStep ? 'Finish' : 'Next' }}
          skipProps={{ onClick: skipTour, 'aria-label': 'Skip tour' }}
          tooltipProps={{
            style: {
              position: 'fixed', top: pos.top, left: pos.left,
              zIndex: 10001, pointerEvents: 'auto',
            },
          }}
        />
      </div>,
      document.body,
    );
  };

  return (
    <TutorialContext.Provider value={ctxValue}>
      {renderOverlay()}
      {children}
    </TutorialContext.Provider>
  );
};

export default observer(TutorialTour);
